import { useEffect, useRef, useState } from 'react';

/** True while the user has moved the pointer / pressed a key recently. */
export function useActivity(timeoutMs = 4000): boolean {
  const [active, setActive] = useState(true);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    const wake = () => {
      setActive(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setActive(false), timeoutMs);
    };
    wake();
    const events = ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    events.forEach((e) => window.addEventListener(e, wake, { passive: true }));
    return () => {
      window.clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, wake));
    };
  }, [timeoutMs]);
  return active;
}

/**
 * Keeps the screen awake while the page is visible (Screen Wake Lock API, where supported).
 * `enabled` false (switched off by its schedule) releases it, so the device's own sleep
 * settings can turn the display off.
 */
export function useWakeLock(enabled = true): void {
  useEffect(() => {
    if (!enabled || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let disposed = false;
    const request = async () => {
      if (document.visibilityState !== 'visible' || disposed) return;
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        // Denied (battery saver, no user gesture yet) – harmless.
      }
    };
    void request();
    const onVisible = () => void request();
    document.addEventListener('visibilitychange', onVisible);
    // Some browsers only grant after a gesture.
    window.addEventListener('pointerdown', onVisible, { once: true });
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, [enabled]);
}

export function toggleFullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  else void document.documentElement.requestFullscreen?.().catch(() => undefined);
}
